/* FindFuneralHomes.jsx — iter92 rebuild
   - Google Places API via /api/locator/search (auto-cached 7d)
   - 10/25/50/100 mile radius selector
   - Rating + review count
   - DB overlay: "Already Partner / Status / Owner"
   - 4 actions per result: View Details · Invite Funeral Home · Save to CRM · Create Partner Invite
   - Mobile-first cards
   - RP + Admin can use action buttons; everyone can View Details */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  MapPin, Search, Phone, ExternalLink, Navigation, Mail, LocateFixed,
  Building2, TreePine, Star, ShieldCheck, BadgeCheck, Send, Bookmark,
  Link2, Eye, X, Copy,
} from 'lucide-react';
import { Navbar, Footer } from '../components/Navbar';
import { usePageSEO } from '../lib/usePageSEO';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABELS = {
  all: 'Funeral homes & cemeteries',
  funeral_home: 'Funeral homes',
  cemetery: 'Cemeteries',
};

const RADIUS_OPTIONS = [10, 25, 50, 100];

function isFuneralHome(r) {
  const t = r.primary_type || '';
  return t === 'funeral_home' || (r.types || []).includes('funeral_home');
}

function TypeBadge({ r }) {
  if (isFuneralHome(r)) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold text-[hsl(var(--primary)/0.72)]">
        <Building2 className="w-3 h-3" /> Funeral home
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold text-[hsl(var(--accent))]">
      <TreePine className="w-3 h-3" /> Cemetery
    </span>
  );
}

function RatingStars({ rating, count }) {
  if (!rating || !count) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--primary)/0.75)]">
      <Star className="w-3.5 h-3.5 fill-[hsl(var(--accent))] stroke-[hsl(var(--accent))]" />
      <strong className="text-[hsl(var(--primary))]">{Number(rating).toFixed(1)}</strong>
      <span className="text-[hsl(var(--primary)/0.55)]">({count.toLocaleString()})</span>
    </span>
  );
}

function PartnerStatusBadge({ ep }) {
  if (!ep) return null;
  const isApproved = ep.partner_status === 'approved';
  const tone = isApproved
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : 'bg-amber-50 border-amber-200 text-amber-900';
  const label = isApproved ? 'Already Partner' : `Pending (${ep.partner_status || 'unknown'})`;
  return (
    <div
      className={`mt-3 rounded-lg border px-3 py-2 ${tone}`}
      data-testid="partner-status-badge"
    >
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold flex items-center gap-1.5">
        <BadgeCheck size={13} />
        {label}
      </div>
      {ep.email && (
        <div className="text-[12px] mt-0.5 truncate">
          Owned by <span className="font-mono">{ep.email}</span>
        </div>
      )}
    </div>
  );
}

export default function FindFuneralHomes() {
  usePageSEO({
    title: 'Find Funeral Homes & Cemeteries Near You — MyLeaf Legacy',
    description: 'Locate trusted funeral homes, cemeteries, and memorial providers in your area. Search by city, ZIP, or your current location.',
    path: '/find-funeral-homes',
  });

  const { user } = useAuth();
  const canAct =
    !!user &&
    ((user.account_type === 'regional_partner' && user.partner_status === 'approved') ||
      user.account_type === 'platform_admin' ||
      user.is_super_admin);

  const [configured, setConfigured] = useState(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [results, setResults] = useState([]);
  const [searchedFor, setSearchedFor] = useState('');
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/locator/status`)
      .then(({ data }) => setConfigured(Boolean(data.configured)))
      .catch(() => setConfigured(false));
  }, []);

  const runSearch = async (payload, label) => {
    setLoading(true);
    setError(null);
    setResults([]);
    setSearchedFor(label);
    try {
      const { data } = await axios.post(`${API}/locator/search`, {
        ...payload,
        kind,
        radius_miles: radiusMiles,
      });
      setResults(data.results || []);
      if (!data.results || data.results.length === 0) {
        setError('No listings found for that location. Try a nearby city or ZIP.');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (detail?.message) setError(detail.message);
      else setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q.length < 2) return;
    runSearch({ query: q }, q);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setError('Your browser does not support geolocation.');
      return;
    }
    setLocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        runSearch(
          { lat: pos.coords.latitude, lng: pos.coords.longitude },
          'your current location',
        );
      },
      (err) => {
        setLocating(false);
        setError(
          err.code === 1
            ? 'Location permission denied. You can search by city or ZIP instead.'
            : 'Could not determine your location. Please try a city or ZIP.',
        );
      },
      { enableHighAccuracy: false, maximumAge: 300000, timeout: 10000 },
    );
  };

  const saveToCRM = async (r) => {
    if (!canAct) { toast.error('Regional Partners and admins only.'); return; }
    setBusyId(r.place_id);
    try {
      const { data } = await axios.post(`${API}/locator/leads`, {
        place_id: r.place_id, name: r.name, address: r.address,
        phone: r.phone, website: r.website, latitude: r.latitude,
        longitude: r.longitude, rating: r.rating,
        user_rating_count: r.user_rating_count, primary_type: r.primary_type,
      }, { withCredentials: true });
      toast.success(data.was_new ? 'Saved to your CRM' : 'Already in your CRM');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setBusyId(null);
    }
  };

  const createPartnerInvite = async (r) => {
    if (!canAct) { toast.error('Regional Partners and admins only.'); return; }
    const target = window.prompt(
      `Generate a Partner Invite link for ${r.name}.\n\nEnter the funeral home's contact email (we won't send anything — you'll just get a copyable link to share via SMS, WhatsApp, etc.):`,
      '',
    );
    if (!target) return;
    setBusyId(r.place_id);
    try {
      const { data } = await axios.post(`${API}/locator/invite-from-place`, {
        place_id: r.place_id, name: r.name, address: r.address,
        phone: r.phone, website: r.website,
        target_email: target.trim(),
        send_email: false,
      }, { withCredentials: true });
      try { await navigator.clipboard.writeText(data.invite_url); }
      catch { /* ignore */ }
      toast.success('Invite link copied to clipboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create invite failed');
    } finally {
      setBusyId(null);
    }
  };

  if (configured === false) {
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="find-page">
        <Navbar />
        <div className="flex-1">
          <section className="ml-section-sacred">
            <div className="ml-container max-w-3xl">
              <div className="ml-card border-dashed text-center" data-testid="locator-not-configured">
                <div className="ml-overline">Locator offline</div>
                <h2 className="font-serif text-2xl text-[hsl(var(--primary))] mt-2">
                  Live search isn't connected yet.
                </h2>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-3 max-w-md mx-auto leading-relaxed">
                  This locator is powered by Google Places. Once our team adds the API key,
                  you'll see real funeral homes and cemeteries near you here.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <a href="mailto:hello@myleaflegacy.com" className="ml-btn-outline">
                    <Mail className="w-4 h-4" /> Email our care team
                  </a>
                  <Link to="/" className="ml-btn-outline">Back to home</Link>
                </div>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="find-page">
      <Navbar />
      <div className="flex-1">
        <section className="ml-section-sacred">
          <div className="ml-container max-w-4xl">
            {/* Header */}
            <div className="text-center">
              <div className="inline-flex w-14 h-14 rounded-full bg-[hsl(var(--accent))]/12 items-center justify-center">
                <MapPin className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.4} />
              </div>
              <div className="ml-overline mt-6">Locator</div>
              <h1 className="font-serif font-light text-[34px] sm:text-5xl mt-5 tracking-tight text-[hsl(var(--primary))]">
                Find funeral homes
                <span className="block italic text-[hsl(var(--primary)/0.7)]">
                  and cemeteries near you.
                </span>
              </h1>
              <p className="mt-6 ml-caption max-w-lg mx-auto">
                Search by city, ZIP, or use your current location.
              </p>
            </div>

            {/* Kind tabs */}
            <div className="mt-10 flex justify-center">
              <div
                className="inline-flex p-1 rounded-full bg-[hsl(var(--surface))] border border-[hsl(var(--primary)/0.12)]"
                data-testid="locator-kind-tabs"
              >
                {Object.entries(KIND_LABELS).map(([k, label]) => (
                  <button
                    key={k} type="button"
                    onClick={() => setKind(k)}
                    data-testid={`locator-kind-${k}`}
                    className={
                      'px-3 sm:px-4 py-2 rounded-full text-[10px] sm:text-[11px] tracking-[0.22em] uppercase font-semibold transition ' +
                      (kind === k
                        ? 'bg-[hsl(var(--primary))] text-[#F7F3E8]'
                        : 'text-[hsl(var(--primary)/0.65)] hover:text-[hsl(var(--primary))]')
                    }
                  >
                    {label.split(' ')[0]}
                  </button>
                ))}
              </div>
            </div>

            {/* Radius selector */}
            <div className="mt-5 flex justify-center items-center gap-2 flex-wrap text-xs" data-testid="locator-radius-tabs">
              <span className="text-[hsl(var(--primary)/0.55)] uppercase tracking-[0.16em] font-semibold text-[10px]">Radius</span>
              {RADIUS_OPTIONS.map((m) => (
                <button
                  key={m} type="button"
                  onClick={() => setRadiusMiles(m)}
                  data-testid={`locator-radius-${m}`}
                  className={
                    'px-3 py-1 rounded-full text-[11px] font-semibold transition ' +
                    (radiusMiles === m
                      ? 'bg-[hsl(var(--accent))] text-[hsl(var(--primary))]'
                      : 'bg-[hsl(var(--surface))] text-[hsl(var(--primary)/0.65)] hover:text-[hsl(var(--primary))]')
                  }
                >
                  {m} mi
                </button>
              ))}
            </div>

            {/* Search */}
            <form onSubmit={onSubmit} className="mt-8 max-w-xl mx-auto" data-testid="find-search-form">
              <div className="flex items-center gap-2 border-b border-[hsl(var(--primary)/0.25)] pb-2 focus-within:border-[hsl(var(--primary))]">
                <Search className="w-4 h-4 text-[hsl(var(--primary)/0.5)] shrink-0" strokeWidth={1.5} />
                <input
                  type="text" value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="City, ZIP, or address…"
                  className="flex-1 bg-transparent border-0 py-2 text-base sm:text-lg font-light text-[hsl(var(--primary))] placeholder:text-[hsl(var(--primary)/0.4)] outline-none min-w-0"
                  data-testid="find-search-input"
                />
                <button
                  type="submit"
                  disabled={loading || query.trim().length < 2}
                  className="ml-btn-primary shrink-0"
                  data-testid="find-search-submit"
                >
                  {loading ? 'Searching…' : 'Search'}
                </button>
              </div>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={useMyLocation}
                  disabled={locating || loading}
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))] hover:text-[hsl(var(--primary))] transition-colors"
                  data-testid="find-use-location"
                >
                  <LocateFixed className="w-3.5 h-3.5" />
                  {locating ? 'Getting your location…' : 'Use my current location'}
                </button>
              </div>
            </form>

            {error && (
              <div
                className="mt-8 text-center text-sm text-[hsl(var(--destructive))] font-light"
                data-testid="find-error"
              >
                {error}
              </div>
            )}

            {results.length > 0 && (
              <div className="mt-12" data-testid="find-results">
                <div className="text-center mb-8">
                  <div className="ml-overline">Showing</div>
                  <div className="font-serif text-xl text-[hsl(var(--primary))] mt-1">
                    {results.length} {KIND_LABELS[kind].toLowerCase()} within {radiusMiles} mi of {searchedFor}
                  </div>
                </div>
                <ul className="space-y-4">
                  {results.map((r) => (
                    <li
                      key={r.place_id}
                      className="ml-card p-5 sm:p-6"
                      data-testid={`locator-result-${r.place_id}`}
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <TypeBadge r={r} />
                            <h3 className="font-serif text-xl sm:text-2xl text-[hsl(var(--primary))] mt-2 leading-tight">
                              {r.name}
                            </h3>
                            {r.address && (
                              <p className="text-sm text-[hsl(var(--primary)/0.75)] mt-2 leading-relaxed">
                                {r.address}
                              </p>
                            )}
                            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm items-center">
                              <RatingStars rating={r.rating} count={r.user_rating_count} />
                              {r.phone && (
                                <a
                                  href={`tel:${r.phone.replace(/\s/g, '')}`}
                                  className="inline-flex items-center gap-1.5 text-[hsl(var(--primary)/0.8)] hover:text-[hsl(var(--accent))]"
                                  data-testid={`locator-phone-${r.place_id}`}
                                >
                                  <Phone className="w-3.5 h-3.5" />
                                  {r.phone}
                                </a>
                              )}
                              {r.website && (
                                <a
                                  href={r.website}
                                  target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-[hsl(var(--primary)/0.8)] hover:text-[hsl(var(--accent))]"
                                  data-testid={`locator-website-${r.place_id}`}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  Website
                                </a>
                              )}
                            </div>
                            <PartnerStatusBadge ep={r.existing_partner} />
                          </div>
                          {r.directions_url && (
                            <a
                              href={r.directions_url}
                              target="_blank" rel="noopener noreferrer"
                              className="ml-btn-outline shrink-0 text-xs sm:text-sm"
                              data-testid={`locator-directions-${r.place_id}`}
                            >
                              <Navigation className="w-4 h-4" />
                              <span className="hidden sm:inline">Directions</span>
                            </a>
                          )}
                        </div>

                        {/* Action row */}
                        <div className="flex flex-wrap gap-2" data-testid={`locator-actions-${r.place_id}`}>
                          <button
                            type="button"
                            onClick={() => setDetailsOpen(r)}
                            className="ml-btn-outline text-xs px-3 py-1.5"
                            data-testid={`act-view-${r.place_id}`}
                          >
                            <Eye size={14} /> View Details
                          </button>
                          {!r.existing_partner && canAct && (
                            <>
                              <button
                                type="button"
                                onClick={() => setInviteOpen(r)}
                                disabled={busyId === r.place_id}
                                className="ml-btn-primary text-xs px-3 py-1.5"
                                data-testid={`act-invite-${r.place_id}`}
                              >
                                <Send size={14} /> Invite Funeral Home
                              </button>
                              <button
                                type="button"
                                onClick={() => createPartnerInvite(r)}
                                disabled={busyId === r.place_id}
                                className="ml-btn-outline text-xs px-3 py-1.5"
                                data-testid={`act-link-${r.place_id}`}
                              >
                                <Link2 size={14} /> Create Partner Invite
                              </button>
                              <button
                                type="button"
                                onClick={() => saveToCRM(r)}
                                disabled={busyId === r.place_id}
                                className="ml-btn-outline text-xs px-3 py-1.5"
                                data-testid={`act-save-${r.place_id}`}
                              >
                                <Bookmark size={14} /> Save to CRM
                              </button>
                            </>
                          )}
                          {r.existing_partner && (
                            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold text-emerald-700 px-2 py-1.5">
                              <ShieldCheck size={13} /> Already in MyLeaf
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!searchedFor && !error && results.length === 0 && (
              <div
                className="mt-14 text-center font-serif italic text-[hsl(var(--primary)/0.6)]"
                data-testid="find-placeholder"
              >
                Try a ZIP code, a city, or your current location.
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />

      {detailsOpen && (
        <DetailsModal r={detailsOpen} onClose={() => setDetailsOpen(null)} />
      )}
      {inviteOpen && (
        <InviteModal
          r={inviteOpen}
          onClose={() => setInviteOpen(null)}
          onSent={() => { setInviteOpen(null); }}
        />
      )}
    </div>
  );
}

function DetailsModal({ r, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="details-modal"
    >
      <div
        className="bg-[hsl(var(--card))] rounded-2xl max-w-md w-full p-6 border border-[hsl(var(--primary)/0.18)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <TypeBadge r={r} />
            <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">{r.name}</h3>
          </div>
          <button onClick={onClose} className="text-[hsl(var(--primary)/0.6)]" data-testid="details-close">
            <X size={20} />
          </button>
        </div>
        {r.address && <p className="text-sm text-[hsl(var(--primary)/0.8)] mb-3 leading-relaxed">{r.address}</p>}
        <div className="space-y-2 text-sm">
          <RatingStars rating={r.rating} count={r.user_rating_count} />
          {r.phone && <div className="text-[hsl(var(--primary))]"><Phone size={13} className="inline mr-2"/>{r.phone}</div>}
          {r.website && (
            <div>
              <a href={r.website} target="_blank" rel="noopener noreferrer"
                 className="text-[hsl(var(--accent))] underline break-all">{r.website}</a>
            </div>
          )}
          {r.google_maps_url && (
            <div>
              <a href={r.google_maps_url} target="_blank" rel="noopener noreferrer"
                 className="text-[hsl(var(--accent))] underline">View on Google Maps →</a>
            </div>
          )}
          {(r.latitude && r.longitude) && (
            <div className="text-[11px] font-mono text-[hsl(var(--primary)/0.55)] pt-2 border-t border-[hsl(var(--primary)/0.08)] mt-3">
              {Number(r.latitude).toFixed(5)}, {Number(r.longitude).toFixed(5)}
            </div>
          )}
          {r.place_id && (
            <div className="text-[10px] font-mono text-[hsl(var(--primary)/0.4)] truncate">
              place_id: {r.place_id}
            </div>
          )}
        </div>
        <PartnerStatusBadge ep={r.existing_partner} />
      </div>
    </div>
  );
}

function InviteModal({ r, onClose, onSent }) {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e?.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error('Enter a valid email address.');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/locator/invite-from-place`, {
        place_id: r.place_id, name: r.name, address: r.address,
        phone: r.phone, website: r.website,
        target_email: email.trim(),
        target_business_name: r.name,
        note: note.trim() || null,
        send_email: true,
      }, { withCredentials: true });
      if (data.email_delivered) {
        toast.success(`Invitation emailed to ${email.trim()}`);
      } else {
        try { await navigator.clipboard.writeText(data.invite_url); }
        catch { /* ignore */ }
        toast.warning('Email delivery offline — link copied to clipboard so you can send manually.');
      }
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invitation failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="invite-modal"
    >
      <form
        onSubmit={send}
        onClick={(e) => e.stopPropagation()}
        className="bg-[hsl(var(--card))] rounded-2xl max-w-md w-full p-6 border border-[hsl(var(--primary)/0.18)] shadow-2xl"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="ml-overline">Invite to MyLeaf</div>
            <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">{r.name}</h3>
            {r.address && <p className="text-xs text-[hsl(var(--primary)/0.6)] mt-1">{r.address}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-[hsl(var(--primary)/0.6)]" data-testid="invite-close">
            <X size={20} />
          </button>
        </div>
        <label className="ml-label">Contact email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="manager@example.com"
          className="ml-input"
          required
          data-testid="invite-email-input"
        />
        <label className="ml-label mt-3">Personal note (optional)</label>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Hi — we'd love to bring you into the MyLeaf partner network…"
          className="ml-input"
          data-testid="invite-note-input"
        />
        <div className="mt-5 grid grid-cols-2 gap-3">
          <button type="button" onClick={onClose}
                  className="ml-btn-outline text-xs"
                  data-testid="invite-cancel">
            Cancel
          </button>
          <button type="submit" disabled={sending}
                  className="ml-btn-primary text-xs"
                  data-testid="invite-submit">
            {sending ? 'Sending…' : <><Send size={14}/> Send invitation</>}
          </button>
        </div>
      </form>
    </div>
  );
}
